import type { CSSProperties } from 'react';
import { PaymentReturnNotice, PaymentStatusPill, readHostedCheckoutReturnState } from '@triedge/payments';
import { HeartIcon, SearchIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  useListFacultyMembersQuery,
} from '../../../state/domainRouterApi';
import { useDomainRouterQuery } from '../../../state/domainRouterHooks';
import { resolveCognitoProfile } from '../../../utils/cognito-profile';
import {
  createHostedPaymentSession,
} from '../../../utils/payments';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';

const resolveCheckoutUrl = (kind: 'success' | 'cancel'): string => {
  const url = new URL(window.location.href);
  url.searchParams.set('checkout', kind);
  return url.toString();
};

const resolvePaymentProfile = () => resolveCognitoProfile();

const extractPaymentErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  if (error && typeof error === 'object') {
    const message = 'message' in error ? String((error as { message?: unknown }).message ?? '').trim() : '';
    if (message) {
      return message;
    }
    if (Array.isArray((error as { errors?: Array<{ message?: unknown }> }).errors)) {
      const nested = (error as { errors?: Array<{ message?: unknown }> }).errors
        ?.map((entry) => String(entry?.message ?? '').trim())
        .filter(Boolean)
        .join('; ');
      if (nested) {
        return nested;
      }
    }
  }
  return String(error ?? '').trim() || 'Unknown payment error';
};

type Teacher = {
  id: string;
  name: string;
  position: string;
  division: string;
};

type TipHistoryItem = {
  id: string;
  teacherName: string;
  amountCents: number;
  status: string;
  occurredAt: number;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = <T,>(value: unknown): T[] => Array.isArray(value) ? (value as T[]) : [];

const normalizeText = (value: unknown): string =>
  String(value ?? '').trim();

const normalizeOptionalText = (value: unknown): string | null => {
  const normalized = normalizeText(value);
  return normalized || null;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

const formatCurrency = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((value) => value[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

const formatDivision = (value: unknown): string => {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) {
    return 'Faculty';
  }
  if (normalized.includes('girls')) {
    return 'Girls School';
  }
  if (normalized.includes('boys')) {
    return 'Boys School';
  }
  if (normalized.includes('pre')) {
    return 'Preschool';
  }
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

function TipCheckoutModal({
  teacher,
  onClose,
}: {
  teacher: Teacher;
  onClose: () => void;
}) {
  const [tipAmount, setTipAmount] = useState(3600);
  const [message, setMessage] = useState('');
  const [receiptEmail, setReceiptEmail] = useState(resolvePaymentProfile()?.email ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const submitTip = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const profile = resolvePaymentProfile();
      const url = await createHostedPaymentSession({
        moduleId: 'tip-a-teacher',
        operationKey: 'tip_a_teacher_checkout',
        mode: 'payment',
        currency: 'usd',
        successUrl: resolveCheckoutUrl('success'),
        cancelUrl: resolveCheckoutUrl('cancel'),
        customerEmail: receiptEmail,
        customerReferenceId: profile?.userSub || receiptEmail,
        description: `Tip for ${teacher.name}`,
        lineItems: [
          {
            name: `Tip a Teacher • ${teacher.name}`,
            description: 'Parent appreciation gift',
            quantity: 1,
            unitAmountCents: tipAmount,
          },
        ],
        metadata: {
          route: '/parent/portal/tip-a-teacher',
          teacherId: teacher.id,
          teacherName: teacher.name,
          teacherDivision: teacher.division,
          message,
          actorSub: profile?.userSub ?? '',
        },
      });
      window.location.assign(url);
    } catch (error) {
      setErrorMessage(extractPaymentErrorMessage(error));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-[16px] border border-border bg-white p-6 shadow-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="font-body text-[12px] uppercase tracking-wider text-text-secondary">Send a Tip</p>
            <h2 data-testid="parent-tip-checkout-heading" className="mt-1 font-heading text-[24px] font-semibold text-iron-grey">{teacher.name}</h2>
            <p className="mt-1 text-sm text-text-secondary">{teacher.position} · {teacher.division}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-text-secondary transition-colors hover:bg-muted"
            aria-label="Close tip checkout"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          {[1800, 3600, 5400, 7200].map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => setTipAmount(amount)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tipAmount === amount
                  ? 'bg-primary text-white'
                  : 'border border-border bg-white text-iron-grey hover:border-primary'
              }`}
            >
              ${amount / 100}
            </button>
          ))}
        </div>

        <label className="mb-4 block text-sm font-medium text-iron-grey">
          Message
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="mt-2 min-h-24 w-full rounded-[10px] border border-border px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="Optional note"
          />
        </label>

        <label className="mb-6 block text-sm font-medium text-iron-grey">
          Receipt email
          <input
            value={receiptEmail}
            onChange={(event) => setReceiptEmail(event.target.value)}
            className="mt-2 w-full rounded-[10px] border border-border px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="parent@example.com"
          />
        </label>

        {errorMessage ? (
          <SupportErrorMessage className="mb-4">
            {errorMessage}
          </SupportErrorMessage>
        ) : null}

        <button
          type="button"
          onClick={() => void submitTip()}
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-primary px-4 py-3 font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          <HeartIcon className="h-4 w-4" aria-hidden="true" />
          {submitting ? 'Redirecting…' : 'Continue to payment'}
        </button>
      </div>
    </div>
  );
}

function ParentTipCatalogPage() {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const facultyQuery = useListFacultyMembersQuery({ filter: { status: 'active' }, limit: 200 });

  const teachers = useMemo<Teacher[]>(() => {
    const items = facultyQuery.data?.items ?? [];
    return items.map((faculty) => ({
      id: String(faculty.id ?? ''),
      name: normalizeOptionalText(faculty.displayName)
        ?? [normalizeText(faculty.firstName), normalizeText(faculty.lastName)].filter(Boolean).join(' ')
        ?? 'Faculty member',
      position: 'Faculty',
      division: formatDivision(faculty.primaryDivisionId),
    }));
  }, [facultyQuery.data?.items]);

  const filteredTeachers = useMemo(
    () =>
      teachers.filter((teacher) =>
        teacher.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [searchQuery, teachers],
  );
  const hostedReturnState = useMemo(
    () => readHostedCheckoutReturnState(location.search),
    [location.search],
  );

  if (facultyQuery.isLoading) {
    return (
      <div
        data-testid="parent-tip-source-faithful-root"
        data-route-family="source-faithful"
        data-route-ready="true"
        className="source-faithful-shell"
        style={{ '--ystnj-source-faithful-max-width': '1120px' } as CSSProperties}
      >
        <div className="source-faithful-container">
          <div className="h-72 animate-pulse rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="parent-tip-source-faithful-root"
      data-extracted-from="source-faithful:parent-tip-a-teacher-v1.0"
      data-route-family="source-faithful"
      data-route-ready="true"
      className="source-faithful-shell"
      style={{ '--ystnj-source-faithful-max-width': '1120px' } as CSSProperties}
    >
      <div className="source-faithful-container">
        <div className="mb-8 space-y-1">
          <p className="font-body text-[12px] uppercase tracking-wider text-text-secondary">Show Your Appreciation</p>
          <h1 className="font-heading text-[24px] font-semibold text-iron-grey">Tip a Teacher</h1>
          <p className="text-sm text-text-secondary">
            Faculty shown here are sourced from the live faculty roster.
          </p>
        </div>

        {facultyQuery.isError ? (
          <SupportErrorMessage className="rounded-lg px-4 py-3 text-sm">
            Faculty records could not be loaded for the tip catalog.
          </SupportErrorMessage>
        ) : (
          <>
            <PaymentReturnNotice
              state={hostedReturnState}
              noticeKey="parent-tip-a-teacher"
              successTitle="Tip payment handed off successfully"
              successBody="Stripe returned successfully. Tip history will reflect the reconciled payment state once processing completes."
              cancelTitle="Tip payment was not completed"
              cancelBody="No tip payment was finalized. You can stay on this teacher list or reopen checkout when ready."
              guidanceBody="Tip history is the authoritative record after reconciliation. If the updated gift is not visible immediately, reopen Tip History after the payment event finishes processing."
              primaryAction={
                <a
                  href="/parent/portal/tip-a-teacher/history"
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-iron-grey shadow-sm"
                >
                  View Tip History
                </a>
              }
            />
            <label className="relative mb-8 block max-w-md">
              <SearchIcon size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                placeholder="Search teachers..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full rounded-[8px] border border-border bg-surface-light px-11 py-2.5 font-body text-[14px] text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
            </label>

            {filteredTeachers.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredTeachers.map((teacher) => (
                  <div
                    key={teacher.id}
                    data-testid={`parent-tip-teacher-card-${teacher.id}`}
                    data-teacher-name={teacher.name}
                    className="flex flex-col items-center rounded-[12px] border border-border bg-surface-light p-5 text-center shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:shadow-md"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                      {getInitials(teacher.name)}
                    </div>
                    <p className="mt-3 font-title text-[16px] font-semibold text-iron-grey">{teacher.name}</p>
                    <p className="mt-1 font-body text-[12px] uppercase tracking-wider text-text-secondary">
                      {teacher.position}
                    </p>
                    <span className="mt-2 rounded-full bg-primary/10 px-2.5 py-0.5 font-body text-[12px] text-primary">
                      {teacher.division}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedTeacher(teacher)}
                      className="group mt-4 flex items-center justify-center gap-1.5 rounded-[6px] border border-primary bg-transparent px-4 py-1.5 text-[13px] font-medium text-primary transition-colors duration-200 hover:bg-primary hover:text-white"
                    >
                      <HeartIcon size={14} className="text-coral transition-colors duration-200 group-hover:text-white" />
                      Send a Tip
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-8 font-body text-text-secondary">No faculty found matching "{searchQuery}".</p>
            )}
          </>
        )}
      </div>

      {selectedTeacher ? <TipCheckoutModal teacher={selectedTeacher} onClose={() => setSelectedTeacher(null)} /> : null}
    </div>
  );
}

function ParentTipHistoryPage() {
  const historyQuery = useDomainRouterQuery('listTipParentHistory', {});

  const history = useMemo<TipHistoryItem[]>(() => {
    const items = asArray<Record<string, unknown>>(asRecord(historyQuery.data).items);
    return items.map((entry, index) => {
      return {
        id: String(entry.id ?? `tip-history-${index}`),
        teacherName: normalizeText(entry.teacherName) || 'Faculty member',
        amountCents: toNumber(entry.amountCents),
        status: normalizeOptionalText(entry.eventType)?.replace(/\./g, ' ') || 'processed',
        occurredAt: toNumber(entry.occurredAt),
      };
    });
  }, [historyQuery.data]);

  return (
    <div
      className="source-faithful-shell"
      data-testid="parent-tip-source-faithful-history"
      data-route-family="source-faithful"
      data-route-ready="true"
      style={{ '--ystnj-source-faithful-max-width': '960px' } as CSSProperties}
    >
      <div className="source-faithful-container">
        <div className="mb-8 space-y-1">
          <p className="font-body text-[12px] uppercase tracking-wider text-text-secondary">Show Your Appreciation</p>
          <h1 className="font-heading text-[24px] font-semibold text-iron-grey">Tip History</h1>
          <p className="text-sm text-text-secondary">
            History is sourced from the stabilized tip payment read model and scoped to the current parent account.
          </p>
        </div>

        {historyQuery.isLoading ? (
          <div className="h-64 animate-pulse rounded-xl bg-muted" />
        ) : historyQuery.isError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Tip history is temporarily unavailable.
          </div>
        ) : (
          <div className="overflow-hidden rounded-[12px] border border-border bg-white shadow-sm">
            <div className="source-faithful-tableScroll">
              <div className="min-w-[620px]">
                <div className="grid grid-cols-[1.4fr,0.8fr,1fr,0.8fr] gap-4 border-b border-border bg-muted/40 px-6 py-3 text-[12px] font-semibold uppercase tracking-wider text-text-secondary">
                  <span>Teacher</span>
                  <span>Amount</span>
                  <span>Date</span>
                  <span>Status</span>
                </div>
                {history.length > 0 ? history.map((entry) => (
                  <div key={entry.id} className="grid grid-cols-[1.4fr,0.8fr,1fr,0.8fr] gap-4 border-b border-border px-6 py-4 text-sm last:border-b-0">
                    <span className="font-semibold text-iron-grey">{entry.teacherName}</span>
                    <span className="text-iron-grey">{formatCurrency(entry.amountCents)}</span>
                    <span className="text-text-secondary">
                      {entry.occurredAt > 0
                        ? new Date(entry.occurredAt * 1000).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                        : '--'}
                    </span>
                    <PaymentStatusPill status={entry.status} />
                  </div>
                )) : (
                  <div className="px-6 py-6 text-sm text-text-secondary">
                    No tip payments are recorded for this family yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ParentTipATeacherSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<ParentTipCatalogPage />} />
      <Route path="history" element={<ParentTipHistoryPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
